'use client';

import Icon from '@/components/ui/AppIcon';

interface ContactActionsProps {
  variant?: 'card' | 'detail';
  phoneNumber?: string;
  address?: string;
  mechanicName?: string;
  onCallClick?: () => void;
  onDirectionsClick?: () => void;
  onQuoteClick?: () => void;
  className?: string;
}

const ContactActions = ({
  variant = 'card',
  phoneNumber = '(555) 123-4567',
  address = '',
  mechanicName = 'Mechanic',
  onCallClick,
  onDirectionsClick,
  onQuoteClick,
  className = ''
}: ContactActionsProps) => {
  const handleCall = () => {
    if (onCallClick) {
      onCallClick();
    } else {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleDirections = () => {
    if (onDirectionsClick) {
      onDirectionsClick();
    } else if (address) {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
    }
  };

  const handleQuote = () => {
    if (onQuoteClick) {
      onQuoteClick();
    }
  };

  if (variant === 'card') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <button
          onClick={handleCall}
          className="flex items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97] flex-1"
          aria-label={`Call ${mechanicName}`}
        >
          <Icon name="PhoneIcon" size={16} />
          <span className="hidden sm:inline">Call</span>
        </button>

        <button
          onClick={handleDirections}
          className="flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2 text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]"
          aria-label={`Get directions to ${mechanicName}`}
        >
          <Icon name="MapPinIcon" size={18} />
        </button>

        <button
          onClick={handleQuote}
          className="flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2 text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]"
          aria-label={`Request quote from ${mechanicName}`}
        >
          <Icon name="DocumentTextIcon" size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <button
        onClick={handleCall}
        className="w-full flex items-center justify-center space-x-3 rounded-lg bg-primary px-6 py-4 font-body text-base font-medium text-primary-foreground shadow-elevation-sm transition-smooth hover:opacity-90 active:scale-[0.97]"
      >
        <Icon name="PhoneIcon" size={20} />
        <span>Call Now</span>
        <span className="font-mono text-sm opacity-90">{phoneNumber}</span>
      </button>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handleDirections}
          className="flex items-center justify-center space-x-2 rounded-lg border border-border bg-card px-4 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:bg-muted hover:text-primary active:scale-[0.97]"
        >
          <Icon name="MapPinIcon" size={18} />
          <span>Directions</span>
        </button>

        <button
          onClick={handleQuote}
          className="flex items-center justify-center space-x-2 rounded-lg border border-border bg-card px-4 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:bg-muted hover:text-primary active:scale-[0.97]"
        >
          <Icon name="DocumentTextIcon" size={18} />
          <span>Get Quote</span>
        </button>
      </div>

      <div className="rounded-lg bg-muted p-4">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs font-medium text-text-primary mb-1">
              Quick Response Guarantee
            </p>
            <p className="font-body text-xs text-text-secondary leading-relaxed">
              This mechanic typically responds to quote requests within 2 hours during business hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactActions;